/**
 * Removes legacy presentation ownership from merchant-authored rich text.
 * Keeps semantic elements, links, media attributes and table structure intact.
 * @param {ParentNode} root
 */
function normalizeLegacyPresentation(root) {
  root.querySelectorAll('[style]').forEach((element) => element.removeAttribute('style'));

  ['align', 'bgcolor', 'border', 'cellpadding', 'cellspacing'].forEach((attribute) => {
    root.querySelectorAll(`[${attribute}]`).forEach((element) => element.removeAttribute(attribute));
  });
}

/**
 * Removes legacy outer wrappers that should not own policy geometry.
 * @param {HTMLElement} policyBody
 */
function unwrapLegacyPolicyContainers(policyBody) {
  const rte = policyBody.querySelector(':scope > .rte');
  if (!rte) return;

  rte.querySelectorAll(':scope > .shipping-policy, :scope > .returns-policy').forEach((wrapper) => {
    const parent = wrapper.parentNode;
    if (!parent) return;

    while (wrapper.firstChild) {
      parent.insertBefore(wrapper.firstChild, wrapper);
    }

    wrapper.remove();
  });
}

/**
 * Adds responsive behavior to semantic tables without duplicating content.
 * Simple 3–4 column tables can stack on small screens; complex tables remain
 * horizontally scrollable inside the existing semantic table.
 * @param {HTMLTableElement} table
 */
function classifyTable(table) {
  const headerCells = Array.from(table.tHead?.rows?.[0]?.cells || []);
  const bodyRows = Array.from(table.tBodies?.[0]?.rows || []);

  if (headerCells.length < 3 || headerCells.length > 4 || bodyRows.length === 0) return;

  const simple =
    !table.querySelector('[colspan], [rowspan]') &&
    bodyRows.every((row) => row.cells.length === headerCells.length);

  if (!simple) return;

  table.classList.add('rte-table--stack-mobile');

  bodyRows.forEach((row) => {
    Array.from(row.cells).forEach((cell, index) => {
      const label = headerCells[index]?.textContent?.trim();
      if (label) cell.dataset.label = label;
    });
  });
}

/**
 * Keeps horizontal-scroll tables keyboard reachable only when they actually overflow.
 * @param {HTMLElement} wrapper
 */
function syncOverflow(wrapper) {
  const overflow = wrapper.scrollWidth > wrapper.clientWidth + 1;
  wrapper.toggleAttribute('data-overflow', overflow);

  if (overflow) {
    wrapper.tabIndex = 0;
  } else {
    wrapper.removeAttribute('tabindex');
  }
}

const overflowWrapperByObservedElement = new WeakMap();

const tableOverflowObserver =
  'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        const wrappers = new Set();

        entries.forEach((entry) => {
          const wrapper = overflowWrapperByObservedElement.get(entry.target);
          if (wrapper) wrappers.add(wrapper);
        });

        wrappers.forEach((wrapper) => syncOverflow(wrapper));
      })
    : null;

function observeOverflow(wrapper, table, observed) {
  requestAnimationFrame(() => {
    if (wrapper.isConnected) syncOverflow(wrapper);
  });

  if (!tableOverflowObserver) return;

  overflowWrapperByObservedElement.set(wrapper, wrapper);
  overflowWrapperByObservedElement.set(table, wrapper);
  tableOverflowObserver.observe(wrapper);
  tableOverflowObserver.observe(table);
  observed?.add(wrapper);
  observed?.add(table);
}

/**
 * Wraps tables in a horizontal scroll container without changing table semantics.
 * @param {ParentNode} root
 */
function formatTables(root, observed) {
  root.querySelectorAll('table').forEach((table) => {
    if (!(table instanceof HTMLTableElement)) return;

    const existingWrapper = table.parentElement;
    if (existingWrapper?.classList.contains('tfp-size-guide-table__scroll')) {
      classifyTable(table);
      return;
    }

    if (existingWrapper?.classList.contains('rte-table-wrapper')) {
      classifyTable(table);
      observeOverflow(existingWrapper, table, observed);
      return;
    }

    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;

    if (!parent) return;

    parent.insertBefore(wrapper, table);
    wrapper.appendChild(table);
    classifyTable(table);

    observeOverflow(wrapper, table, observed);
  });
}

/**
 * Enhances one rich-text root and returns its overflow-observation cleanup.
 */
export function enhanceRTE(root) {
  const observed = new Set();
  formatTables(root, observed);
  return () => {
    observed.forEach((element) => {
      tableOverflowObserver?.unobserve(element);
      overflowWrapperByObservedElement.delete(element);
    });
    observed.clear();
  };
}

const policyBody = document.querySelector('.shopify-policy__body');
if (policyBody) {
  normalizeLegacyPresentation(policyBody);
  unwrapLegacyPolicyContainers(policyBody);
  formatTables(policyBody);
}
